"use client";

import type { ReactNode } from "react";

/**
 * Right-aligned "Market Overview" toggle that collapses/expands a
 * dashboard widget below it. Used by the Climate and Geopolitics
 * categories — both render the same on/off pill + a max-h transition
 * wrapper around their respective dashboard component.
 *
 * The persisted-state ownership stays in the parent (writes the
 * boolean to localStorage so it survives reloads); the wrapper is
 * pure presentation.
 */
export function DashboardToggleStrip({
  expanded,
  onToggle,
  label,
  children,
}: {
  expanded: boolean;
  onToggle: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <>
      <div className="flex items-center justify-end mb-3">
        <button onClick={onToggle} className="flex items-center gap-2" title={label}>
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">
            {label}
          </span>
          <div
            className={`relative w-9 h-5 rounded-[5px] transition-colors duration-200 ${
              expanded ? "bg-gray-900 dark:bg-white" : "bg-gray-300 dark:bg-gray-600"
            }`}
          >
            <div
              className={`absolute top-[2px] w-4 h-4 rounded-[5px] bg-white dark:bg-gray-900 transition-transform duration-200 ${
                expanded ? "translate-x-[18px]" : "translate-x-[2px]"
              }`}
            />
          </div>
        </button>
      </div>
      <div className={expanded ? "mb-6" : ""}>
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out ${
            expanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          {children}
        </div>
      </div>
    </>
  );
}
